import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUp } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { requireAuth } from "@/lib/auth";
import { useResume } from "@/components/GoogleReturn";
import { ContributorBenefitInput } from "@/components/proposal/BenefitsEdit";

const RELS = ["Resident", "Neighbor / community member", "Homeowner", "Owner or manager"];
const GROUPS = [["owner", "For the owner", "e.g. Lower maintenance calls, a better-kept building"], ["residents", "For residents", "e.g. A safer, drier bathroom"]];

export const MakeItRealButton = ({ visionId, disabled }) => {
  const [open, setOpen] = useState(false);
  const [rel, setRel] = useState("Resident");
  const [b, setB] = useState({ owner: "", residents: "" });
  const [contrib, setContrib] = useState({ text: "", type: "Public recognition" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const show = useCallback(() => setOpen(true), []);
  useResume(`mir:${visionId}`, show);

  const submit = async () => {
    setBusy(true);
    const benefits = Object.fromEntries(Object.entries(b).map(([k, v]) => [k, v.trim() ? [{ text: v.trim() }] : []]));
    benefits.contributors = contrib.type ? [{ text: contrib.text.trim() || contrib.type, type: contrib.type }] : [];
    try {
      const { data } = await api.post(`/visions/${visionId}/proposals`, { relationship: rel, benefits });
      nav(`/m/${data.id}`);
    } catch (e) { toast(errMsg(e, "Couldn't create the proposal. Try again.")); }
    setBusy(false);
  };

  return (
    <>
      <button className="btn-back" disabled={disabled} onClick={() => requireAuth("PlaceBacker It", show, `mir:${visionId}`)} data-testid="make-it-real-button">
        PlaceBacker It <ArrowUp size={16} strokeWidth={2.5} />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto rounded-xl border-none bg-[var(--bone)] p-7 sm:p-9" data-testid="make-it-real-dialog">
          <div className="eyebrow">PlaceBacker It</div>
          <DialogTitle className="font-display text-3xl tracking-tight">Propose this to the owner</DialogTitle>
          <DialogDescription className="text-sm text-[var(--stone-dark)]">We'll split the vision into required repairs (the owner's responsibility) and optional upgrades the community can support. No funds are collected.</DialogDescription>
          <div className="mt-2">
            <div className="eyebrow mb-2">Your relationship to this place</div>
            <div className="flex flex-wrap gap-2" data-testid="relationship-options">
              {RELS.map((r) => (
                <button key={r} onClick={() => setRel(r)} className={`min-h-[40px] rounded-full border px-4 text-sm transition-colors ${rel === r ? "border-[var(--charcoal)] bg-[var(--charcoal)] text-[var(--bone)]" : "border-[var(--line)] hover:border-[var(--charcoal)]"}`} data-testid={`relationship-${r.split(" ")[0].toLowerCase()}`}>{r}</button>
              ))}
            </div>
          </div>
          <div className="grid gap-3">
            <div className="eyebrow">Benefits (optional) · Proposed terms — not a binding agreement</div>
            {GROUPS.map(([k, label, ph]) => (
              <label key={k} className="block">
                <span className="mb-1 block text-sm">{label}</span>
                <input value={b[k]} onChange={(e) => setB({ ...b, [k]: e.target.value })} placeholder={ph} className="h-11 w-full rounded-xl border border-[var(--line)] bg-white/70 px-3 text-sm outline-none focus:border-[var(--charcoal)]" data-testid={`benefit-${k}-input`} />
              </label>
            ))}
            <div><span className="mb-1 block text-sm">For contributors</span><ContributorBenefitInput value={contrib} onChange={setContrib} testid="benefit-contributors" /></div>
          </div>
          <button className="btn-primary mt-2 w-full justify-center" disabled={busy} onClick={submit} data-testid="create-proposal-button">{busy ? "Building proposal…" : "Create proposal"}</button>
        </DialogContent>
      </Dialog>
    </>
  );
};
